// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Sparkles, Smartphone } from 'lucide-react'
import { getLandPagePreviewUrlForIframe } from '@/modules/helpers/storefrontUrls'
import { STOREFRONT_PREVIEW_SANDBOX } from '@/modules/helpers/storefrontPreview'
import { useActivePreview } from './useActivePreview'

const PREVIEW_RELOAD_MIN_INTERVAL_MS = 5000

/** Right-pane storefront preview; the page only mounts it once a hash exists. */
export function ContentPanel() {
    const { previewData } = useActivePreview()
    if (!previewData) {
        return null
    }
    return (
        <div className="bg-muted/30 flex h-full min-h-0 flex-col overflow-hidden">
            <StorefrontPreview
                hash={previewData.finalHash}
                refining={!previewData.imageCurationComplete}
                refreshToken={previewData.refreshToken}
                refreshLabel={previewData.refreshLabel}
            />
        </div>
    )
}

function StorefrontPreview({
    hash,
    refining,
    refreshToken,
    refreshLabel,
}: {
    hash: string
    refining: boolean
    refreshToken?: string
    refreshLabel?: string
}) {
    const [iframeLoaded, setIframeLoaded] = useState(false)
    // Token changes reload the iframe, throttled: pipeline stages can land in
    // bursts, and reloading per change would thrash the preview. The trailing
    // token always applies, so the final state is never missed.
    const [appliedToken, setAppliedToken] = useState(refreshToken)
    const [announcement, setAnnouncement] = useState<string | null>(null)
    const lastAppliedAtRef = useRef(0)
    useEffect(() => {
        // The mount token never applies (state initializes to it), so every
        // apply is a real change and deserves its caption.
        if (refreshToken === appliedToken) return
        const apply = () => {
            lastAppliedAtRef.current = Date.now()
            setAppliedToken(refreshToken)
            // The reload navigates the iframe, so the loading state has to
            // come back until the new document paints.
            setIframeLoaded(false)
            if (refreshLabel) {
                setAnnouncement(refreshLabel)
            }
        }
        const wait = Math.max(
            0,
            PREVIEW_RELOAD_MIN_INTERVAL_MS -
                (Date.now() - lastAppliedAtRef.current),
        )
        const timer = setTimeout(apply, wait)
        return () => clearTimeout(timer)
    }, [refreshToken, refreshLabel, appliedToken])
    useEffect(() => {
        if (!announcement) return
        const timer = setTimeout(() => setAnnouncement(null), 4000)
        return () => clearTimeout(timer)
    }, [announcement])
    const baseUrl = getLandPagePreviewUrlForIframe(hash)
    // An applied-token change navigates the iframe in place so the preview
    // tracks the run instead of freezing at its first paint.
    const previewUrl = appliedToken
        ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}pv=${encodeURIComponent(appliedToken)}`
        : baseUrl

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex h-full flex-col"
        >
            <div className="flex items-center gap-2 border-b px-4 py-3 bg-background">
                <Smartphone className="size-4 text-muted-foreground" />
                <div className="text-sm font-medium">Storefront Preview</div>
                {refining && (
                    <span className="bg-primary/10 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">
                        <motion.span
                            animate={{ opacity: [0.4, 1, 0.4] }}
                            transition={{
                                repeat: Infinity,
                                duration: 1.5,
                                ease: 'easeInOut',
                            }}
                            className="size-1.5 rounded-full bg-primary"
                        />
                        Refining
                    </span>
                )}
            </div>
            <div className="relative flex-1 bg-background">
                <AnimatePresence>
                    {announcement && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="bg-background/95 absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full border px-3 py-1 shadow-sm"
                        >
                            <span className="text-xs text-muted-foreground">
                                Preview updated: {announcement}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>
                {!iframeLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{
                                    repeat: Infinity,
                                    duration: 1,
                                    ease: 'linear',
                                }}
                            >
                                <Sparkles className="mx-auto size-6 text-muted-foreground" />
                            </motion.div>
                            <div className="mt-2 text-xs text-muted-foreground">
                                Loading preview...
                            </div>
                        </div>
                    </div>
                )}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: iframeLoaded ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex h-full items-center justify-center p-4"
                >
                    <div className="border-foreground/10 size-full max-w-sm overflow-hidden rounded-xl border-4 shadow-2xl bg-background">
                        <iframe
                            src={previewUrl}
                            sandbox={STOREFRONT_PREVIEW_SANDBOX}
                            className="size-full"
                            onLoad={() => setIframeLoaded(true)}
                            title="Storefront Preview"
                        />
                    </div>
                </motion.div>
            </div>
        </motion.div>
    )
}
